/**
 * SAEweb – Main App Component
 * Servicio de Atención al Empleado (web)
 */
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './services/AuthContext';

// Layout
import Header from './components/Header';

// Public pages
import EmployeeHome from './pages/EmployeeHome';
import LinksPage from './pages/LinksPage';
import VacationForm from './pages/VacationForm';
import NewEmployee from './pages/NewEmployee';
import OnboardingPage from './pages/OnboardingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ResetConfirmPage from './pages/ResetConfirmPage';

// Admin pages
import AdminLayout from './pages/admin/AdminLayout';
import AdminHome from './pages/admin/AdminHome';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminEmployees from './pages/admin/AdminEmployees';
import AdminVacations from './pages/admin/AdminVacations';
import AdminSubmissions from './pages/admin/AdminSubmissions';
import AdminCourses from './pages/admin/AdminCourses';
import AdminContracts from './pages/admin/AdminContracts';
import AdminOnboarding from './pages/admin/AdminOnboarding';
import AdminSettings from './pages/admin/AdminSettings';
import AdminLinks from './pages/admin/AdminLinks';
import AdminCorreo from './pages/admin/AdminCorreo';

// Gestoría
import GestoriaPage from './pages/GestoriaPage';

function AdminRoute({ children }) {
  const { isAdmin, loading } = useAuth();
  if (loading) return <div className="spinner" />;
  if (!isAdmin) return <Navigate to="/login" replace />;
  return children;
}

// La gestoría no usa la home de empleado: al entrar se le lleva a su panel.
function HomeRoute() {
  const { isGestoria, isAdmin, loading } = useAuth();
  if (loading) return <div className="spinner" />;
  if (isGestoria && !isAdmin) return <Navigate to="/gestoria" replace />;
  return <EmployeeHome />;
}

function AppRoutes() {
  return (
    <>
      <Header />
      <Routes>
        {/* Public */}
        <Route path="/" element={<HomeRoute />} />
        <Route path="/enlaces" element={<LinksPage />} />
        <Route path="/vacaciones" element={<VacationForm />} />
        <Route path="/nuevo-empleado" element={<NewEmployee />} />
        <Route path="/onboarding/:token" element={<OnboardingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/registro" element={<RegisterPage />} />
        <Route path="/recuperar" element={<ResetPasswordPage />} />
        <Route path="/reset-password/:uid/:token" element={<ResetConfirmPage />} />

        {/* Admin */}
        <Route path="/gestion" element={<AdminRoute><AdminLayout /></AdminRoute>}>
          <Route index element={<AdminHome />} />
          {/* Rutas fuera del alcance actual: no aparecen en la portada, pero
              se conservan para poder recuperarlas sin rehacerlas. */}
          <Route path="panel" element={<AdminDashboard />} />
          <Route path="empleados" element={<AdminEmployees />} />
          <Route path="vacaciones" element={<AdminVacations />} />
          <Route path="solicitudes" element={<AdminSubmissions />} />
          <Route path="solicitudes/correo" element={<AdminCorreo />} />
          <Route path="cursos" element={<AdminCourses />} />
          <Route path="contratos" element={<AdminContracts />} />
          <Route path="onboarding" element={<AdminOnboarding />} />
          <Route path="enlaces" element={<AdminLinks />} />
          <Route path="configuracion" element={<AdminSettings />} />
        </Route>

        {/* Gestoría */}
        <Route path="/gestoria" element={<GestoriaPage />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* Hidden credits */}
      <div className="hidden-credits">
        Franquicia Miguel Ceínos · Desarrollado por Óscar Corona
      </div>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppShell />
      </AuthProvider>
    </BrowserRouter>
  );
}

function AppShell() {
  const { isAuthenticated } = useAuth();
  return (
    <div className={`app-layout ${isAuthenticated ? 'app-layout--authenticated' : ''}`}>
      <Toaster
        position="top-right"
        toastOptions={{
          className: 'toast-custom',
          duration: 4000,
          style: {
            fontFamily: 'DM Sans, sans-serif',
            borderRadius: '12px',
          },
        }}
      />
      <AppRoutes />
    </div>
  );
}
